/** The frame every page of a project's briefing sits in: the comments in flight on it
 * (with the way to their conversation), the page itself (commentable by selecting any text), the review bar, and a link
 * to the briefing's history at the very bottom. Earlier versions are read on the
 * history pages, not here. See docs/Y2026-Q4/design-review-mode.md. */
import { type ReactNode, useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { Link } from "react-router-dom";

import { useProjectAgentStatus } from "../api";
import { type ReviewLevel, useReviewComments } from "../api/hooks/useReview";
import { formatWhen } from "../utils/formatWhen";
import { projectStatusHistoryPath } from "../routes/learningAgentPaths";
import { ReviewThreads } from "./ReviewBar";
import { ReviewProvider } from "./ReviewProvider";
import styles from "./Review.module.css";

/** ``level`` is what a selection on this page is a comment on, where the text selected
 * sits in no more specific block. */
export const BriefingFrame = ({
  projectId,
  level,
  children,
}: {
  projectId: string;
  level: ReviewLevel;
  children: ReactNode;
}) => {
  const comments = useReviewComments(projectId ? { project_id: projectId } : null);
  const status = useProjectAgentStatus(projectId || null).data;
  const builtAt = status?.latest_run_at ?? null;
  const applying = status?.applying_review ?? null;
  // When the pass finishes, every part of the briefing it rebuilt is read again.
  const qc = useQueryClient();
  const wasApplying = useRef(false);
  useEffect(() => {
    if (wasApplying.current && !applying) void qc.invalidateQueries();
    wasApplying.current = !!applying;
  }, [applying, qc]);
  const docKey = `project:${projectId}`;
  return (
    <ReviewProvider
      available
      defaultDoc={docKey}
      defaultLevel={level}
      comments={comments.data?.comments ?? []}
      phases={comments.data?.phases}
      briefingVersion={status?.latest_version ?? null}
      projectId={projectId}
      onQuote={null}
    >
      <div className={styles.frame}>
        <ReviewThreads scope={{ project_id: projectId }} projectId={projectId} />
        {children}
        <p className={styles.historyLink} data-review-chrome>
          {builtAt
            ? status?.latest_run_kind === "scheduled"
              ? `Built from the scheduled pass of ${formatWhen(builtAt)} - `
              : `Updated by a review on ${formatWhen(builtAt)} - `
            : "No scheduled pass has worked this project yet - "}
          <Link to={projectStatusHistoryPath(projectId)}>View history</Link>
        </p>
      </div>
    </ReviewProvider>
  );
};
